import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  User,
  Mail,
  FileText,
  DollarSign,
  Calendar,
  AlertCircle,
} from "lucide-react";

const STATUS_COLORS = {
  "Mail Sended": "bg-red-200 text-red-800 border-red-300",
  "Invoice Uploaded": "bg-yellow-300 text-yellow-900 border-yellow-400",
  Troubled: "bg-yellow-700 text-white border-yellow-800",
  "Change Requested": "bg-red-700 text-white border-red-800",
  Approved: "bg-blue-700 text-white border-blue-800",
  Accounting: "bg-purple-700 text-white border-purple-800",
  Paid: "bg-green-600 text-white border-green-700",
  "PAID-TIMEOUT": "bg-emerald-500 text-white border-emerald-600",
  LUCA: "bg-orange-700 text-white border-orange-800",
  "Not Paid": "bg-gray-500 text-white border-gray-600",
  Return: "bg-pink-600 text-white border-pink-700",
  "Auto Created Invoice": "bg-cyan-600 text-white border-cyan-700",
  "OCR Completed": "bg-indigo-600 text-white border-indigo-700",
  InBank: "bg-teal-600 text-white border-teal-700",
};

export function InvoiceCard(props) {
  const {
    name,
    email,
    invoiceId,
    finalAmount,
    status,
    returnReason,
    totalAmount,
    currency,
    invoiceDate,
    recipientName,
    onDetailClick,
    isHorizontal,
  } = props;

  return (
    <Card
      className={cn(
        "group hover:shadow-xl transition-all duration-300 hover:border-primary/50 overflow-hidden",
        isHorizontal && "flex justify-between items-center"
      )}
    >
      {/* Gradient accent bar */}
      <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg mb-1 group-hover:text-primary transition-colors flex items-center gap-2 truncate">
              <User className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{name || recipientName || "Client Yok"}</span>
            </CardTitle>
            <CardDescription className="flex items-center gap-1.5 text-sm truncate">
              <Mail className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{email || "-"}</span>
            </CardDescription>
          </div>
          <Badge
            variant="outline"
            className={cn("flex-shrink-0 text-xs", STATUS_COLORS[status] || "bg-gray-500 text-white")}
          >
            {status}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Invoice Info Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Invoice ID */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Invoice ID</p>
              <p className="font-medium text-sm truncate">{invoiceId || "-"}</p>
            </div>
          </div>

          {/* Final Amount */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
              <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Amount</p>
              <p className="font-bold text-sm text-emerald-600 dark:text-emerald-400">
                {currency || "$"}
                {totalAmount || finalAmount || "0"}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Date */}
        {invoiceDate && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Invoice Date</p>
              <p className="text-sm font-medium">{invoiceDate}</p>
            </div>
          </div>
        )}

        {/* Return Reason */}
        {status === "Return" && returnReason && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-red-700 mb-1">
                Return Reason
              </p>
              <p className="text-xs text-red-600">{returnReason}</p>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-4 border-t bg-muted/20">
        <Button
          onClick={onDetailClick}
          className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
          variant="outline"
        >
          View Details →
        </Button>
      </CardFooter>
    </Card>
  );
}
